import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Sparkles, Upload, GripVertical } from 'lucide-react';
import { gachasApi, raritiesApi, PLACEHOLDER_IMAGE } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { ImageUpload } from '../upload/ImageUpload';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const RARITY_COLORS = {
    Common: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    Uncommon: 'bg-green-500/20 text-green-400 border-green-500/30',
    Rare: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    Epic: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    Legendary: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    Mythical: 'bg-red-500/20 text-red-400 border-red-500/30',
    Monarch: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
};

export const GachaManager = ({ island }) => {
    const { user } = useAuth();
    const [gachas, setGachas] = useState([]);
    const [rarities, setRarities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingGacha, setEditingGacha] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [gachaToDelete, setGachaToDelete] = useState(null);
    const [expandedGacha, setExpandedGacha] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        image_url: '',
        values_by_rarity: []
    });

    useEffect(() => {
        loadRarities();
    }, []);

    useEffect(() => {
        if (island?.id) {
            loadGachas();
        }
    }, [island?.id]);

    const loadRarities = async () => {
        try {
            const response = await raritiesApi.getAll();
            setRarities(response.data.rarities || []);
        } catch (error) {
            console.error('Failed to load rarities:', error);
        }
    };

    const loadGachas = async () => {
        setLoading(true);
        try {
            const response = await gachasApi.getAll(island.id);
            const data = response.data;
            setGachas(Array.isArray(data) ? data : (data.items || []));
        } catch (error) {
            console.error('Failed to load gachas:', error);
            toast.error('Fehler beim Laden der Gachas');
        } finally {
            setLoading(false);
        }
    };

    const initializeFormData = (gacha = null) => {
        if (gacha) {
            setFormData({
                name: gacha.name || '',
                description: gacha.description || '',
                image_url: gacha.image_url || '',
                values_by_rarity: gacha.values_by_rarity || []
            });
        } else {
            // Initialize with all rarities
            const initialValues = rarities.map(rarity => ({
                rarity,
                fields: []
            }));
            setFormData({
                name: '',
                description: '',
                image_url: '',
                values_by_rarity: initialValues
            });
        }
    };

    const handleCreate = async () => {
        if (!formData.name.trim()) return;
        try {
            await gachasApi.create({
                name: formData.name.trim(),
                description: formData.description || '',
                island_id: island.id,
                image_url: formData.image_url || null,
                values_by_rarity: formData.values_by_rarity
            }, user?.discord_id, user?.username);
            setModalOpen(false);
            loadGachas();
        } catch (error) {
            console.error('Failed to create gacha:', error);
            toast.error('Fehler beim Erstellen des Gachas');
        }
    };

    const handleUpdate = async () => {
        if (!formData.name.trim() || !editingGacha) return;
        try {
            await gachasApi.update(editingGacha.id, {
                name: formData.name.trim(),
                description: formData.description || '',
                island_id: island.id,
                image_url: formData.image_url || null,
                values_by_rarity: formData.values_by_rarity
            }, user?.discord_id, user?.username);
            setEditingGacha(null);
            setModalOpen(false);
            toast.success('Gacha aktualisiert');
            loadGachas();
        } catch (error) {
            console.error('Failed to update gacha:', error);
            toast.error('Fehler beim Aktualisieren des Gachas');
        }
    };

    const handleDelete = async () => {
        if (!gachaToDelete) return;
        try {
            await gachasApi.delete(gachaToDelete.id, user?.discord_id, user?.username);
            toast.success('Gacha gelöscht');
            setDeleteModalOpen(false);
            setGachaToDelete(null);
            loadGachas();
        } catch (error) {
            console.error('Failed to delete gacha:', error);
            toast.error('Fehler beim Löschen des Gachas');
        }
    };

    const openCreateModal = () => {
        setEditingGacha(null);
        initializeFormData();
        setModalOpen(true);
    };

    const openEditModal = (gacha) => {
        setEditingGacha(gacha);
        initializeFormData(gacha);
        setModalOpen(true);
    };

    // Drag & Drop handlers
    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItem === null || draggedItem === index) return;

        const newGachas = [...gachas];
        const draggedGacha = newGachas[draggedItem];
        newGachas.splice(draggedItem, 1);
        newGachas.splice(index, 0, draggedGacha);
        
        setGachas(newGachas);
        setDraggedItem(index);
    };

    const handleDragEnd = async () => {
        if (draggedItem === null) return;
        
        const updates = gachas.map((gacha, index) => ({
            id: gacha.id,
            order: index
        }));
        
        try {
            await gachasApi.reorder(updates);
            toast.success('Reihenfolge gespeichert');
        } catch (error) {
            console.error('Failed to reorder:', error);
            loadGachas();
        }
        
        setDraggedItem(null);
    };

    // Value management functions
    const addFieldToRarity = (rarityIndex) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === rarityIndex
                    ? { ...item, fields: [...item.fields, { name: '', value: '' }] }
                    : item
            )
        }));
    };

    const removeFieldFromRarity = (rarityIndex, fieldIndex) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === rarityIndex
                    ? { ...item, fields: item.fields.filter((_, fi) => fi !== fieldIndex) }
                    : item
            )
        }));
    };

    const updateRarityField = (rarityIndex, fieldIndex, key, value) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === rarityIndex
                    ? {
                        ...item,
                        fields: item.fields.map((field, fi) =>
                            fi === fieldIndex ? { ...field, [key]: value } : field
                        )
                    }
                    : item
            )
        }));
    };

    const updateRarityImageUrl = (rarityIndex, imageUrl) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === rarityIndex ? { ...item, image_url: imageUrl } : item
            )
        }));
    };

    const updateRarityMeta = (rarityIndex, isMeta) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === rarityIndex ? { ...item, is_meta: isMeta } : item
            )
        }));
    };

    const updateRarityMaxed = (rarityIndex, isMaxed) => {
        setFormData(prev => ({
            ...prev,
            values_by_rarity: prev.values_by_rarity.map((item, idx) =>
                idx === rarityIndex ? { ...item, is_maxed: isMaxed } : item
            )
        }));
    };

    const ensureRarityExists = (rarity) => {
        const exists = formData.values_by_rarity.find(v => v.rarity === rarity);
        if (!exists) {
            setFormData(prev => ({
                ...prev,
                values_by_rarity: [...prev.values_by_rarity, { rarity, image_url: '', is_meta: false, is_maxed: false, fields: [] }]
            }));
        }
    };

    if (!island) {
        return (
            <div className="p-8 text-center text-gray-500 border border-dashed border-white/10 rounded-xl">
                <Sparkles size={48} className="mx-auto mb-4 text-gray-600" />
                <p>Wähle eine Insel aus, um Gachas zu verwalten</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-['Rajdhani'] font-semibold text-lg text-white">
                        Gachas auf {island.name}
                    </h3>
                    <p className="text-xs text-gray-500">Verwalte Gacha-Optionen mit Seltenheitswerten</p>
                </div>
                <Button
                    onClick={openCreateModal}
                    size="sm"
                    data-testid="create-gacha-btn"
                    className="bg-purple-500 hover:bg-purple-400 text-white"
                >
                    <Plus size={16} className="mr-1" />
                    Neues Gacha
                </Button>
            </div>

            {/* Gachas List */}
            <div className="space-y-3">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Laden...</div>
                ) : gachas.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 border border-dashed border-white/10 rounded-xl">
                        Noch keine Gachas auf dieser Insel
                    </div>
                ) : (
                    gachas.map((gacha, index) => (
                        <div
                            key={gacha.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            data-testid={`gacha-item-${gacha.id}`}
                            className={cn(
                                "rounded-xl border border-white/10 bg-white/5 overflow-hidden transition-opacity",
                                draggedItem === index && "opacity-50"
                            )}
                        >
                            {/* Gacha Header */}
                            <div 
                                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5 transition-colors"
                                onClick={() => setExpandedGacha(expandedGacha === gacha.id ? null : gacha.id)}
                            >
                                {/* Drag Handle */}
                                <div className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-white">
                                    <GripVertical size={18} />
                                </div>
                                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-purple-500/10">
                                    <img
                                        src={gacha.image_url || PLACEHOLDER_IMAGE}
                                        alt={gacha.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-['Rajdhani'] font-semibold text-white truncate">
                                        {gacha.name}
                                    </h4>
                                    {gacha.description && (
                                        <p className="text-sm text-gray-500 truncate">{gacha.description}</p>
                                    )}
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                        {(gacha.values_by_rarity || []).filter(v => v.fields?.length > 0).map((v) => (
                                            <span
                                                key={v.rarity}
                                                className={cn(
                                                    "px-1.5 py-0.5 rounded text-xs border",
                                                    RARITY_COLORS[v.rarity] || 'bg-gray-500/20 text-gray-400'
                                                )}
                                            >
                                                {v.rarity}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); openEditModal(gacha); }}
                                        className="text-gray-400 hover:text-purple-400 hover:bg-purple-500/10"
                                    >
                                        <Pencil size={16} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setGachaToDelete(gacha); 
                                            setDeleteModalOpen(true); 
                                        }}
                                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                    {expandedGacha === gacha.id ? (
                                        <ChevronUp size={20} className="text-gray-500" />
                                    ) : (
                                        <ChevronDown size={20} className="text-gray-500" />
                                    )}
                                </div>
                            </div>

                            {/* Expanded Values */}
                            {expandedGacha === gacha.id && (
                                <div className="border-t border-white/10 p-4 bg-black/20">
                                    <h5 className="text-sm font-medium text-gray-400 mb-3">Werte nach Seltenheit:</h5>
                                    <div className="grid gap-3">
                                        {(gacha.values_by_rarity || []).filter(v => v.fields?.length > 0).map((rarityData) => (
                                            <div key={rarityData.rarity} className={cn(
                                                "p-3 rounded-lg border",
                                                RARITY_COLORS[rarityData.rarity] || 'border-white/10'
                                            )}>
                                                <span className="font-semibold">{rarityData.rarity}</span>
                                                <div className="mt-2 space-y-1">
                                                    {rarityData.fields.map((field, idx) => (
                                                        <div key={idx} className="flex justify-between text-sm">
                                                            <span className="text-gray-400">{field.name}:</span>
                                                            <span className="text-white font-mono">{field.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        {(!gacha.values_by_rarity || gacha.values_by_rarity.filter(v => v.fields?.length > 0).length === 0) && (
                                            <p className="text-sm text-gray-500 italic">Keine Werte definiert</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-xl text-white">
                            {editingGacha ? 'Gacha bearbeiten' : 'Neues Gacha erstellen'}
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-gray-400">Name *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Gacha Name"
                                        data-testid="gacha-name-input"
                                        className="bg-black/40 border-white/10"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-gray-400">Beschreibung</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Optionale Beschreibung..."
                                        rows={2}
                                        className="bg-black/40 border-white/10 resize-none"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-gray-400">Bild</Label>
                                    <ImageUpload
                                        currentImage={formData.image_url}
                                        onUpload={(url) => setFormData(prev => ({ ...prev, image_url: url || '' }))}
                                    />
                                    <div className="text-xs text-gray-500">Oder URL direkt eingeben:</div>
                                    <Input
                                        value={formData.image_url}
                                        onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                        placeholder="https://..."
                                        className="bg-black/40 border-white/10"
                                    />
                                </div>
                            </div>

                            {/* Values by Rarity */}
                            <div className="space-y-4">
                                <Label className="text-gray-400">Werte nach Seltenheit</Label>
                                <p className="text-xs text-gray-600">Definiere verschiedene Werte und Bilder für jede Seltenheitsstufe</p>

                                {rarities.map((rarity, rarityIndex) => {
                                    const rarityData = formData.values_by_rarity.find(v => v.rarity === rarity) || { rarity, image_url: '', fields: [] };
                                    const actualIndex = formData.values_by_rarity.findIndex(v => v.rarity === rarity);

                                    return (
                                        <div 
                                            key={rarity} 
                                            className={cn(
                                                "p-4 rounded-xl border",
                                                RARITY_COLORS[rarity] || 'border-white/10'
                                            )}
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-semibold">{rarity}</span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        if (actualIndex === -1) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                values_by_rarity: [...prev.values_by_rarity, { rarity, image_url: '', is_meta: false, fields: [{ name: '', value: '' }] }]
                                                            }));
                                                        } else {
                                                            addFieldToRarity(actualIndex);
                                                        }
                                                    }}
                                                    className="text-xs"
                                                >
                                                    <Plus size={14} className="mr-1" />
                                                    Feld hinzufügen
                                                </Button>
                                            </div>

                                            {/* Meta & Maxed Checkboxes für diese Seltenheit */}
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="flex items-center gap-2 p-2 rounded bg-orange-500/10 border border-orange-500/20">
                                                    <input
                                                        type="checkbox"
                                                        id={`meta-${rarity}`}
                                                        checked={rarityData.is_meta || false}
                                                        onChange={(e) => {
                                                            if (actualIndex === -1) {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    values_by_rarity: [...prev.values_by_rarity, { rarity, image_url: '', is_meta: e.target.checked, is_maxed: false, fields: [] }]
                                                                }));
                                                            } else {
                                                                updateRarityMeta(actualIndex, e.target.checked);
                                                            }
                                                        }}
                                                        className="w-4 h-4 rounded border-orange-500/50 bg-black/40 text-orange-500"
                                                    />
                                                    <label htmlFor={`meta-${rarity}`} className="text-sm text-orange-400 cursor-pointer">
                                                        Meta
                                                    </label>
                                                </div>
                                                <div className="flex items-center gap-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/20">
                                                    <input
                                                        type="checkbox"
                                                        id={`maxed-${rarity}`}
                                                        checked={rarityData.is_maxed || false}
                                                        onChange={(e) => {
                                                            if (actualIndex === -1) {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    values_by_rarity: [...prev.values_by_rarity, { rarity, image_url: '', is_meta: false, is_maxed: e.target.checked, fields: [] }]
                                                                }));
                                                            } else {
                                                                updateRarityMaxed(actualIndex, e.target.checked);
                                                            }
                                                        }}
                                                        className="w-4 h-4 rounded border-yellow-500/50 bg-black/40 text-yellow-500"
                                                    />
                                                    <label htmlFor={`maxed-${rarity}`} className="text-sm text-yellow-400 cursor-pointer">
                                                        Maxed
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Image URL für diese Seltenheit */}
                                            <div className="mb-3">
                                                <Input
                                                    value={rarityData.image_url || ''}
                                                    onChange={(e) => {
                                                        if (actualIndex === -1) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                values_by_rarity: [...prev.values_by_rarity, { rarity, image_url: e.target.value, fields: [] }]
                                                            }));
                                                        } else {
                                                            updateRarityImageUrl(actualIndex, e.target.value);
                                                        }
                                                    }}
                                                    placeholder={`Bild URL für ${rarity} (optional)`}
                                                    className="bg-black/40 border-white/10 h-9 text-sm"
                                                />
                                            </div>

                                            {actualIndex !== -1 && rarityData.fields.length > 0 ? (
                                                <div className="space-y-2">
                                                    {rarityData.fields.map((field, fieldIndex) => (
                                                        <div key={fieldIndex} className="flex gap-2">
                                                            <Input
                                                                value={field.name}
                                                                onChange={(e) => updateRarityField(actualIndex, fieldIndex, 'name', e.target.value)}
                                                                placeholder="Feldname"
                                                                className="flex-1 bg-black/40 border-white/10 h-9 text-sm"
                                                            />
                                                            <Input
                                                                value={field.value}
                                                                onChange={(e) => updateRarityField(actualIndex, fieldIndex, 'value', e.target.value)}
                                                                placeholder="Wert"
                                                                className="flex-1 bg-black/40 border-white/10 h-9 text-sm"
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => removeFieldFromRarity(actualIndex, fieldIndex)}
                                                                className="h-9 w-9 text-gray-400 hover:text-red-400"
                                                            >
                                                                <Trash2 size={14} />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-600 italic">Keine Werte für diese Seltenheit</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </ScrollArea>

                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={editingGacha ? handleUpdate : handleCreate}
                            disabled={!formData.name.trim()}
                            data-testid="save-gacha-btn"
                            className="bg-purple-500 hover:bg-purple-400 text-white"
                        >
                            {editingGacha ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="max-w-sm bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-xl text-red-400">
                            Gacha löschen?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-gray-400">
                        Möchtest du <span className="text-white font-semibold">"{gachaToDelete?.name}"</span> wirklich löschen?
                    </p>
                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-400 text-white"
                        >
                            Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
